import React, { FC, Fragment, useState } from 'react';
import moment from 'moment';
import { find, get, findIndex } from 'lodash';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { closeModal } from '@src/redux/actions/modal';
import {
  SUBSCRIPTION_BASIC_BENEFITS,
  SUBSCRIPTION_UNLIMITED_BENEFITS,
  CYCLE_LABELS,
  SUBSCRIBE_ERROR_MESSAGES,
} from '@src/constants';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { isSubscriptionUnlimited, isSubscriptionBasic, isSlotBasedSubscription } from '@src/util/planTypes';
import {
  CustomerSubscriptionCycleEnum,
  UpgradeSubscriptionDataQuery,
  useUpgradeSubscriptionDataQuery,
  useUpgradeSubscriptionMutation,
} from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import { trackEvent } from '@src/metrics';
import {
  BenefitsList,
  DetailsList,
  FooterInfoBlock,
  ModalInfo,
  ModalActions,
  ModalContainer,
  ModalTitle,
} from '../components';
import { UpgradeDetailsInterface } from './upgradeDetails.interface';
import { UpgradeSubscriptionModalProps } from './props';

const getBenefitsBasedOnSubscriptionPlan = (planType): string[] => {
  if (isSubscriptionBasic(planType)) {
    return SUBSCRIPTION_BASIC_BENEFITS;
  }

  if (isSubscriptionUnlimited(planType)) {
    return SUBSCRIPTION_UNLIMITED_BENEFITS;
  }

  return [];
};

const getUpgradeDetails = (
  availableCurrencies: UpgradeSubscriptionDataQuery['availableCurrencies'],
  nextPlan: UpgradeSubscriptionDataQuery['subscriptionPlans'][0]['variants'][0],
  nextPaymentDate: string,
): UpgradeDetailsInterface[] => {
  const cycle = nextPlan.cycle || CustomerSubscriptionCycleEnum.MONTHLY;
  const currency = availableCurrencies?.find(({ code }) => code === nextPlan.currency.toUpperCase());

  return [
    {
      name: 'Base fee',
      shouldDisplay: typeof nextPlan.price === 'number' && !!currency,
      value: !!currency && (
        <Fragment>
          <strong>
            {currency.symbol}
            {nextPlan.price}
          </strong>
          {CYCLE_LABELS[cycle]}
        </Fragment>
      ),
    },
    {
      name: 'Each extra opening',
      shouldDisplay: typeof nextPlan.openingActivationPrice === 'number' && !!currency,
      value: !!currency && (
        <Fragment>
          <strong>
            {currency.symbol}
            {nextPlan.openingActivationPrice}
          </strong>
          {CYCLE_LABELS[cycle]}
        </Fragment>
      ),
    },
    {
      name: 'Next payment date',
      shouldDisplay: !!nextPaymentDate,
      value: moment(nextPaymentDate).format('DD MMM YYYY'),
    },
  ].filter(({ shouldDisplay }) => !!shouldDisplay);
};

export const UpgradeSubscriptionModal: FC<UpgradeSubscriptionModalProps> = ({
  nextFunction,
  hasOpeningsLimitReached,
}) => {
  const dispatch = useAppDispatch();
  const [isSubscriptionUpgraded, setIsSubscriptionUpgraded] = useState(false);
  const {
    loading: isQueryLoading,
    error,
    data,
  } = useUpgradeSubscriptionDataQuery({
    onError: () => dispatch(setErrorFlashMessage()),
  });
  const [upgradeSubscription, { loading: isMutationLoading }] = useUpgradeSubscriptionMutation({
    onCompleted: () => {
      dispatch(setSuccessFlashMessage('Subscription plan has successfully upgraded!'));
      setIsSubscriptionUpgraded(true);
    },
    onError: error => {
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach(graphQLError => {
          const code: keyof typeof SUBSCRIBE_ERROR_MESSAGES = get(graphQLError, 'extensions.exception.code');
          dispatch(setErrorFlashMessage(SUBSCRIBE_ERROR_MESSAGES[code] || undefined));
        });
      }
    },
  });

  if (isQueryLoading) {
    return <ModalContainer isLoading />;
  }

  if (error) {
    dispatch(closeModal());

    return null;
  }

  const {
    availableCurrencies,
    subscriptionPlans,
    customer: {
      planType,
      id: customerId,
      planInfo: { cycle, nextPaymentDate, trialEndsAt },
    },
  } = data;

  const plansWithoutSlotBasedSubscriptions = subscriptionPlans.filter(
    ({ planType }) => !isSlotBasedSubscription(planType),
  );
  const currentPlanIndex = findIndex(plansWithoutSlotBasedSubscriptions, { planType });

  const nextPlanIndex = currentPlanIndex + 1;
  const visiblePlanIndex =
    isSubscriptionUpgraded || nextPlanIndex >= plansWithoutSlotBasedSubscriptions.length // upgraded or it is last possible plan
      ? currentPlanIndex
      : nextPlanIndex;
  const { planType: nextPlanType, name, variants: nextVariants } = plansWithoutSlotBasedSubscriptions[visiblePlanIndex];
  const nextPlan = find(nextVariants, { cycle });

  const benefits = getBenefitsBasedOnSubscriptionPlan(nextPlanType);
  const details = getUpgradeDetails(availableCurrencies, nextPlan, nextPaymentDate);

  if (isSubscriptionUpgraded) {
    return (
      <ModalContainer>
        <ModalTitle>Subscription plan upgraded!</ModalTitle>
        <ModalInfo>You’ve successfully upgraded to the {name} subscription plan.</ModalInfo>
        <BenefitsList benefits={benefits} />
        <DetailsList details={details} />
        <ModalActions
          actions={[
            {
              name: 'Continue',
              onClick: () => {
                dispatch(closeModal());
                nextFunction();
              },
            },
          ]}
          isPaddingDisabled={!nextFunction}
        />
        <FooterInfoBlock
          isPaddingDisabled={!nextFunction}
          text="You will be charged the price difference for the remainder of your current billing period."
        />
      </ModalContainer>
    );
  }

  return (
    <ModalContainer>
      <ModalTitle>{hasOpeningsLimitReached ? 'Openings limit reached!' : 'Upgrade subscription plan'}</ModalTitle>
      <ModalInfo>
        {hasOpeningsLimitReached
          ? `In order to continue you’ll need to upgrade to the ${name} subscription plan.`
          : 'Best value for growing businesses that hire frequently and want more applicants.'}
      </ModalInfo>
      <BenefitsList benefits={benefits} />
      <DetailsList details={details} />
      <ModalActions
        actions={[
          {
            isDisabled: isMutationLoading,
            name: 'Upgrade',
            onClick: () => {
              upgradeSubscription({
                variables: {
                  planType: nextPlanType,
                  subscriptionCycle: cycle,
                },
              });

              trackEvent({
                context: MetricContext.BILLING,
                interaction: MetricAction.UPGRADE_SUBSCRIPTION,
                payload: {
                  'Customer': customerId,
                  'New Subscription Plan': nextPlanType,
                  'Old Subscription Plan': planType,
                  'Trial Ends At': trialEndsAt,
                },
              });
            },
          },
          {
            buttonAppearance: 'secondary',
            isDisabled: isMutationLoading,
            name: 'Close',
            onClick: () => dispatch(closeModal()),
          },
        ]}
        isPaddingDisabled={!nextFunction}
      />
      <FooterInfoBlock
        isPaddingDisabled={!nextFunction}
        text="You will be charged the price difference for the remainder of your current billing period."
      />
    </ModalContainer>
  );
};
